import React, { ReactNode, useEffect } from "react";
import bridge from "../../turbo/bridge";
import { useSelector } from "react-redux";

import { State } from "../../@types";
import ToggleGroupFavoriteButton from "../shared/favoriteButtons/ToggleGroupFavoriteButton";

import classNames from "classnames";
import ToggleGroupSubscribe from "../shared/ToggleGroupSubscribeButton";
import DropDown from "../shared/DropDown";
import { isEmpty } from "lodash";
import ToggleStreamSubscribeItem from "../activitystreams/ToggleStreamStubscribeItem";

interface PageTitleTypes {
  title: string;
  subtitle?: string;
  renderHeadlineTools?: (() => React.ReactNode | null) | null;
  children?: React.ReactNode;
  setDocumentTitle?: boolean;
}

/*
 * Main "dumb" PageTitle component
 * Styles are duplicated in app/views/layouts/_content.html.haml
 */
export default function PageTitle({
  title,
  subtitle,
  renderHeadlineTools,
  children,
  setDocumentTitle = true,
}: PageTitleTypes) {
  if (renderHeadlineTools)
    console.warn("Prop renderHeadlineTools on PageTitle is deprecated");

  useEffect(() => {
    if (setDocumentTitle && !window.isApp) {
      document.title = `${title} | ${Preload.current_network.name}`;
    }

    if (window.isApp) {
      bridge.setPageTitle(title);

      const handleTurboRender = () => {
        bridge.setPageTitle(title);
      };

      document.addEventListener("turbo:render", handleTurboRender);
      return () => {
        document.removeEventListener("turbo:render", handleTurboRender);
      };
    }
  }, [title]);

  // On server-rendered pages the layout also emits a title (#static-page-title
  // in layouts/_content.html.haml). Whenever this component renders one, that
  // static heading has to give way, otherwise the page shows the title twice.
  // Previously only window.setTixxtPageTitle did this, so it worked exclusively
  // on pages that go through Backbone.
  useEffect(() => {
    if (window.isApp) return;

    const hideStaticTitle = () => {
      document.getElementById("static-page-title")?.classList.add("hidden!");
    };

    hideStaticTitle();

    // Unlike the portal roots next to it, #static-page-title is NOT
    // data-turbo-permanent: every Turbo navigation swaps in a fresh node while
    // this component stays mounted. Without re-applying, the second
    // server-rendered page of a session would show its title twice again.
    document.addEventListener("turbo:render", hideStaticTitle);

    return () => {
      document.removeEventListener("turbo:render", hideStaticTitle);
      document.getElementById("static-page-title")?.classList.remove("hidden!");
    };
  }, []);

  if (window.isApp) return null;

  return (
    <div className={"tixxt__pagetitle"}>
      <h1>{title}</h1>
      {subtitle ? <small>{subtitle}</small> : null}
      {children}
    </div>
  );
}

/*
 * Shows either the group or stream name or the given title
 * Allows user to favorite, subscribe, subscribe rss feed or leave the current group
 * Allows user to subscribe the current stream
 */
export function ContextPageTitle({
  groupSlug,
  streamSlug,
  title: titleProp,
}: {
  groupSlug?: string;
  streamSlug?: string;
  title?: string | null;
}) {
  const group = useSelector((state: State) =>
    groupSlug ? state.groups.bySlug?.[groupSlug] : null,
  );
  const title = group?.name || titleProp;
  if (!title) return null;

  const items: ReactNode[] = [];

  if (((groupSlug && group) || streamSlug) && !Preload?.page_title) {
    if (groupSlug && group) {
      if (Preload?.current_member?.authentication_token)
        items.push(
          <DropDown.Item
            key={"atom"}
            url={`/groups/${groupSlug}/activitystreams/${groupSlug}/activities.atom?auth_token=${Preload.current_member.authentication_token}`}
            text={I18n.t("composer.editor.layout.abo")}
            data={{ "data-turbo": false }}
          />,
        );

      items.push(
        <ToggleGroupSubscribe key={"toggle_group_sub"} groupSlug={groupSlug} />,
      );

      if (group.can?.leave)
        items.push(
          <DropDown.Item
            key={"leave"}
            text={I18n.t("js.directory.group_row.leave_group")}
            url={`/groups/${groupSlug}/join`}
            data={{
              "data-method": "delete",
              "data-confirm": I18n.t(
                "js.directory.group_row.leave_group_confirmation",
              ),
            }}
            dividerTop
          />,
        );
    }

    if (streamSlug && streamSlug !== "all") {
      items.push(
        <ToggleStreamSubscribeItem
          key={"toggle_stream_sub"}
          streamSlug={streamSlug}
        />,
      );
    }
  }

  return (
    <PageTitle title={title}>
      {(!isEmpty(items) || (groupSlug && group)) && (
        <div>
          {!isEmpty(items) && (
            <>
              <DropDown.Toggle
                buttonType="normal"
                className="text-muted"
                ariaLabel={I18n.t("js.pagetitle.options_label")}
                small
              />
              <DropDown alignment="start">{items}</DropDown>
            </>
          )}
          {groupSlug && group && (
            <ToggleGroupFavoriteButton groupSlug={groupSlug} />
          )}
        </div>
      )}
    </PageTitle>
  );
}

export function Subtitle({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <h2 className={classNames("uppercase font-semibold text-sm", className)}>
      {children}
    </h2>
  );
}
